/**
 * shared/protocol.ts — the wire. Every shape that crosses a process boundary, in one file.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * Four processes on two machines read this file: the browser, the app on cloud-2 (:8912), the worker
 * on amd-server, and the origin on amd-server (:8962). There is deliberately no code generation and
 * no runtime schema library — the types are the contract and `npm run check` is the enforcement,
 * which works because one tsconfig covers all six source trees. A per-directory tsconfig would let a
 * contract change typecheck on one side and not the other, and this is the repo where "one side"
 * means "a different computer".
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── VOX-LAW 14, which is why the readers at the bottom exist ────────────────────────────────────
 *
 * **Absent ≠ zero on the wire. A missing numeric field is a hard failure, never coerced.** Adopted
 * from audiobook's LAW 9 and it is worse here than there: a missing `noiseFloorDb` coerced to 0 is a
 * room floor of 0 dBFS, which makes `thresholdDbFor()` return +12 dB, which makes every detector
 * downstream report a take of pure silence — with no error anywhere, and with the recording of
 * somebody's mother sitting perfectly intact on disk behind it. `num()`, `str()`, `bool()` and
 * `enumOf()` throw. `optNum()` exists for genuinely optional fields and it returns `null`, never 0.
 *
 * ── The type-level laws ─────────────────────────────────────────────────────────────────────────
 *
 * **VOX-LAW 10.** `HeldOut<T>` is a branded type, not a boolean field. A boolean gets copied by a
 * spread; a brand does not compile. See its note below for why the design's literal spelling is
 * written this way instead.
 *
 * **VOX-LAW 13.** Everything on `BrowserMetrics` is advisory and is never written to the database.
 * The type is separate from `TakeRow` for exactly that reason: two numbers with the same name and
 * different provenance in one object is how a browser's guess ends up in an envelope.
 */

import type { Actor } from "./actor.ts";
import type { SeedKind } from "./voice/keys.ts";
import type { SessionShape } from "./voice/script.ts";

// ─── vocabulary ──────────────────────────────────────────────────────────────

/**
 * `voice_sessions.state`. **`permission-denied` is a first-class state and not an
 * "abandoned at step 1".**
 *
 * That distinction is not bookkeeping: Appendix D.1's falsifier is a completion rate below 70% on the
 * first five real family sessions, and §3.3 argues that the single likeliest place a non-technical
 * session dies is a permission bubble she never saw. If those two outcomes share a row value, the
 * falsifier fires and nobody can tell which of two completely different fixes it is asking for.
 */
export type SessionState =
  | "new"
  | "permission-denied"
  | "room-check"
  | "recording"
  | "uploading"
  | "scoring"
  | "provisioning"
  | "revealed"
  | "deferred"
  | "abandoned";

export const SESSION_STATES: readonly SessionState[] = [
  "new",
  "permission-denied",
  "room-check",
  "recording",
  "uploading",
  "scoring",
  "provisioning",
  "revealed",
  "deferred",
  "abandoned",
];

/** `takes.kind`. Maps from `Prompt.kind` in `shared/voice/script.ts`; see the note there. */
export type TakeKind = "candidate" | "heldOut" | "consent" | "baseline";
export const TAKE_KINDS: readonly TakeKind[] = ["candidate", "heldOut", "consent", "baseline"];

/**
 * `voices.status`.
 *
 *   session      an anon-created voice, invisible to the fleet until claimed AND approved (§2.4)
 *   provisional  every recording in the session was DSP-processed (VOX-LAW 2b) — it works, it is flagged
 *   unverified   published with no second opinion. §10 and §17 Q3: A NORMAL STATE, NOT A BLOCKED ONE.
 *   published    the objective winner, with a human agreeing
 *   withdrawn    pulled from the manifest. VOX-LAW 23: this deletes no clip anywhere in the fleet.
 */
export type VoiceStatus = "session" | "provisional" | "unverified" | "published" | "withdrawn";
export const VOICE_STATUSES: readonly VoiceStatus[] = [
  "session",
  "provisional",
  "unverified",
  "published",
  "withdrawn",
];

/**
 * `voices.consentState`. **VOX-LAW 22: a voice reaches the fleet manifest only from `verified` or
 * `unverified-manual`.** `pending` is the normal state while spark-1 is down — §3.8 makes the ASR
 * match asynchronous precisely so that a GPU outage delays the *fleet* and not the *family*.
 */
export type ConsentState = "pending" | "verified" | "unverified-manual";
export const CONSENT_STATES: readonly ConsentState[] = ["pending", "verified", "unverified-manual"];

/** `takes.voidedReason`. A voided take is never repaired (VOX-LAW 5). */
export type VoidedReason = "clock-drift" | "context-suspended" | "cue-overlap" | "visibility-change";

/** `takes.ineligibleReason`. Ineligible for a SEED; still usable as held-out and for coaching. */
export type IneligibleReason =
  /** VOX-LAW 2b — `getSettings()` still reported NS/AEC/AGC on. */
  | "dsp-processed"
  /** §5.3 — true peak at or above `TRUE_PEAK_REJECT_DBTP`. A hard gate, not a ranking. */
  | "clipped"
  /** VOX-LAW 27 — `resemble-perth` positive. A seed is never derived from generated audio. */
  | "watermarked"
  /** §3.11 — diarisation found more than one speaker. The silent path that would clone two people. */
  | "two-speakers"
  /** Shorter than `SEED_POLICY.minSeconds` of continuous speech after trim. */
  | "too-short"
  /** VOX-LAW 10 — held out by construction. Not a defect; the most valuable recording in the session. */
  | "held-out";

// ─── VOX-LAW 10: held out is a type ──────────────────────────────────────────

declare const heldOutBrand: unique symbol;

/**
 * **VOX-LAW 10. Every similarity number is computed against a held-out take that entered no candidate
 * seed. Held-out is a distinct TypeScript type, not a boolean field.**
 *
 * The design writes this as `type HeldOut = Take & { readonly __heldOut: unique symbol }`. That exact
 * spelling does not compile — `unique symbol` is only legal as the type of a `const` or a `readonly
 * static`, not of an object-literal property — so the intent is expressed with a declared brand
 * symbol, which is the same thing and does compile. The property does not exist at runtime.
 *
 * The point is unchanged and it is the reason the law is a law: a boolean gets copied. `{...take,
 * kind: "candidate"}` silently launders a held-out recording into the seed it is supposed to be
 * measured against, and the resulting similarity number is a measurement of a thing against itself —
 * which looks excellent, which is the failure.
 */
export type HeldOut<T> = T & { readonly [heldOutBrand]: true };

/**
 * The ONE place a take becomes held-out. Deliberately a function and not a cast, so `git grep
 * asHeldOut` finds every site where the guarantee is asserted — there should be exactly one, in the
 * session planner, driven by `Prompt.kind`.
 */
export const asHeldOut = <T>(take: T): HeldOut<T> => take as HeldOut<T>;

// ─── the actor and the boot payload ──────────────────────────────────────────

/**
 * `GET /api/v1/session` — **VOX-LAW 20: THE ONLY ROUTE THAT SETS A COOKIE.** No media route ever
 * sets one, and the cookie is never the durable handle to a session; a claim token is.
 */
export interface SessionEnvelope {
  actor: Actor;
  /** Everything the client needs that it must not read from the environment (`shared/config.ts`). */
  app: {
    publicOrigin: string;
    /** `false` while `VOX_TTS_MODE=none`. The studio then offers the deferred path up front (§3.14). */
    ttsConfigured: boolean;
    /** Who the copy's `{helper}` is on this deployment. */
    helperName: string;
  };
}

// ─── dependency reporting ────────────────────────────────────────────────────

/**
 * spark-1's `/health`, stored verbatim on the session row before prompt 1 (§3.14).
 *
 * Stored rather than merely consulted, because a session recorded during an outage must be legible
 * *afterwards* as an outage rather than as an unexplained quality outlier. `dtype` in particular is
 * §8.5's re-baseline trigger: a documented `float32` → `bfloat16` change is a re-baseline event, not
 * an alarm, and the only way to know which one happened is to have written it down at the time.
 *
 * Fields beyond these four are kept in `raw` rather than dropped. A host that starts reporting
 * something new is a thing we want to still have a copy of six weeks later.
 */
export interface HealthReport {
  modelLoaded: boolean;
  dtype: string | null;
  variants: readonly string[];
  /** Round-trip time in ms, measured here. Not reported by the host. */
  rttMs: number;
  raw: Readonly<Record<string, unknown>>;
}

/** `GET /api/v1/status` — the dependency report. **VOX-LAW 28: `/health` is liveness, local only.** */
export interface StatusReport {
  ok: boolean;
  env: "dev" | "prod";
  /** null when unreachable. Unreachable is not an error here; it is the expected steady state. */
  tts: HealthReport | null;
  asr: { reachable: boolean; rttMs: number | null };
  origin: { reachable: boolean; rttMs: number | null };
  queue: Readonly<Record<string, { queued: number; running: number }>>;
  disk: { dataDirFreeBytes: number; storageDirFreeBytes: number | null };
  /** `shared/config.ts` warnings — degraded-but-running states. Printed at boot, never silent. */
  warnings: readonly string[];
  /**
   * **The two ways a session ends worse than it had to with every machine up.** `routes/status.ts`
   * derives it; `PayoffReadiness` there carries the derivation.
   *
   * ⚠️ It is on the contract rather than being one of that file's additive extras because both halves
   * are otherwise **invisible by construction**. A session that reached the end of the chain with a
   * finished clone and no payoff sentence takes §3.14's deferred ending, which is warm, designed and
   * indistinguishable from the GPU being asleep — so *"every session in the product ends deferred"*
   * looked exactly like *"spark-1 is down again"* on this page, for as long as nobody counted. And a
   * missing prompt recording is a 404 on a static asset that `prompt.tsx` swallows on purpose
   * (VOX-LAW 16b), so the studio silently becomes "read this" instead of "repeat after me" with
   * nothing in any log.
   */
  payoff: {
    /** Deferred, with a rev on a host, and no payoff sentence. Nothing but the words is missing. */
    sessionsWaitingOnASentence: number;
    sessionsRevealed: number;
    promptAudio: { recorded: number; expected: number; complete: boolean };
  };
}

// ─── starting a session ──────────────────────────────────────────────────────

export interface CreateSessionRequest {
  /** The only metadata a family types. Becomes `voices.label` and the copy's `{name}`. */
  subjectName: string;
  seedKind: SeedKind;
  shape: SessionShape;
  /** §3.13 — authored by the OTHER person, before she starts. She never sees it. */
  revealText?: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  /** VOX-LAW 20. The durable handle. Shown on screen in large type at the end, not at the start. */
  claimToken: string;
  /** `/live/:sessionToken` for the helper (§3.15). Offered as a link she can be told to send. */
  liveUrl: string;
  /** Probed BEFORE prompt 1, so the deferred path is a choice and not a discovery (§3.14). */
  health: HealthReport | null;
}

/**
 * `track.getSettings()`, **verbatim** (VOX-LAW 2).
 *
 * A setting we asked for is never assumed to have applied. The three DSP flags are optional here
 * because a browser is allowed not to report them at all, and `undefined` must stay distinguishable
 * from `false`: "the browser says AGC is off" and "the browser will not say" are different facts, and
 * only the first one makes a take seed-eligible.
 */
export interface TrackSettings {
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  sampleRate?: number;
  channelCount?: number;
  deviceId?: string;
  groupId?: string;
  latency?: number;
  [key: string]: unknown;
}

/** `POST /api/v1/sessions/:id/room-check` — §3.4, and every one of these answers "room or model?". */
export interface RoomCheckRequest {
  /** Median level over the quiet regions of the three seconds, dBFS. Becomes every take's threshold. */
  noiseFloorDb: number;
  /** Crude direct-to-reverberant ratio from the decay after her utterance, dB. §5.2 says why it matters. */
  drrDb: number;
  chimeHeard: boolean;
  callOnSameDevice: boolean;
  /** Stable-ish hash of UA + device ids. DNSMOS is grouped by this and NEVER compared across it. */
  deviceFingerprint: string;
  trackSettings: TrackSettings;
}

// ─── takes ───────────────────────────────────────────────────────────────────

/**
 * The multipart fields beside the WAV on `POST /api/v1/sessions/:id/takes`.
 *
 * ⚠️ **The route streams to the origin on amd-server and never spools to disk on cloud-2**, which has
 * 56 G free at 72%. `test/no-spool.test.ts` asserts the route holds no full body in memory or on disk.
 * These fields therefore arrive BEFORE the file part, and a client that sends them after has produced
 * a request the server has to buffer to understand — which is the same defect wearing a different hat.
 *
 * ── Why `sha` and `bytes` are on the wire and not computed server-side ──────────────────────────
 *
 * The origin (`origin/src/index.ts`) has exactly one write verb, `PUT /v1/blob/:kind/:sha.:ext`, and
 * it names the address in the path. Streaming means cloud-2 opens that outbound connection *before*
 * it has seen the last byte of the inbound one — so it must know the sha in advance or it has to hold
 * the whole file to compute one, which is the spool the route exists to avoid. The browser is the
 * only party that can supply it: one `crypto.subtle.digest` over bytes the encode worker has already
 * assembled, off the main thread, while she is reading the next prompt.
 *
 * This is **not** trust. The origin re-hashes what actually lands and refuses a mismatch with 422,
 * leaving `tmp/` empty and the final path untouched (VOX-LAW 11). Because the sha is taken at the
 * encoder — before IndexedDB, before the network — that verify covers the *whole* path a recording
 * travels, including a byte flipped in staging on the way back out. Hashing at drain time instead
 * would have stored the corruption faithfully under its own address, which is the failure with no
 * error anywhere that VOX-LAW 11 is written against.
 */
export interface TakeUpload {
  promptId: string;
  attempt: number;
  kind: TakeKind;
  /**
   * sha256 of the **exact WAV bytes**, 64 lowercase hex. The blob's address, not a checksum beside it.
   * Computed in `client/src/studio/encode.worker.ts` immediately after `writeWav`, before the buffer
   * is transferred. Verified at the origin, never trusted (VOX-LAW 11).
   */
  sha: string;
  /** Length of the same bytes. Verified alongside the sha — a length is what a truncation shows up in. */
  bytes: number;
  sampleRate: number;
  bitDepth: 16 | 24;
  /** Where the labelled region starts inside the continuous capture, ms. VOX-LAW 4 constrains it. */
  regionStartMs: number;
  regionEndMs: number;
  /** Measured from this take's own pre-roll (§4.7). NOT a fleet constant; see shared/audio/edges.ts. */
  noiseFloorDb: number;
  /** How far behind the drain was when this landed. The old ring "overflow abort", demoted (§4.1). */
  drainLagMs: number;
  trackSettings: TrackSettings;
  /** Set when the client voided it; the server stores the reason and never repairs the take. */
  voidedReason?: VoidedReason;
  /** VOX-LAW 13 — advisory, for `/live` and `/admin`. Never written to `envelopes`. */
  browser?: BrowserMetrics;
}

/**
 * What the browser measured, kept structurally separate from everything of record.
 *
 * **VOX-LAW 13. The browser's numbers are advisory and are never written to the database.** Every
 * score of record is computed on amd-server from the stored bytes, by a scorer whose identity is a
 * hash of its own weights and library versions (VOX-LAW 12). These exist so `/live` can show Karti a
 * level meter while she is speaking, and for nothing else.
 */
export interface BrowserMetrics {
  peakDbfs: number;
  /** Incremental BS.1770 block state from the worklet, posted at 20 Hz. */
  shortTermLufs: number;
  clippedSamples: number;
}

export interface TakeSummary {
  id: string;
  promptId: string;
  attempt: number;
  kind: TakeKind;
  durationMs: number;
  bytes: number;
  bitDepth: 16 | 24;
  sampleRate: number;
  /** Null until the worker has measured it. **Absent ≠ zero** — see `optNum` below. */
  onsetMs: number | null;
  tailMs: number | null;
  thresholdDb: number;
  seedEligible: boolean;
  ineligibleReason: IneligibleReason | null;
  voidedReason: VoidedReason | null;
  createdAt: number;
}

// ─── the studio's view of itself ─────────────────────────────────────────────

/** `GET /api/v1/sessions/:id` — resolvable by the owning actor OR by a claim token (VOX-LAW 20). */
export interface SessionView {
  id: string;
  state: SessionState;
  subjectName: string;
  seedKind: SeedKind;
  shape: SessionShape;
  scriptVersion: number;
  /** The ordered prompt ids for this shape, resolved at creation so a script bump cannot re-plan a
   *  session that is halfway through. */
  plan: readonly string[];
  takes: readonly TakeSummary[];
  /** Corrections spent, out of `COACH_BUDGET`. Rendered on `/live`, never on the family path. */
  coachSpent: number;
  createdAt: number;
  completedAt: number | null;
  /** Last recording → first audio, ms. Budget 75 s p90 (§3.12). Null until there is audio. */
  revealMs: number | null;
  health: HealthReport | null;
}

/**
 * `POST /api/v1/sessions/:id/complete` — **which does NOT answer a `SessionView`.**
 *
 * It lived in `server/src/routes/sessions.ts` because this file had no type for it when that route
 * was written, and the client meanwhile assumed the route answered a `SessionView` and parsed it as
 * one. The parse threw on every single call — there is no `id`, no `subjectName` and no `takes` in
 * this shape — so `<Wait>` logged a failure and dropped the answer to the one request that decides
 * how the session ends. It is here now because it is a contract between two processes, and this file
 * is where those live.
 *
 * `path` is the fork: `reveal` when something is actually listening, `deferred` otherwise. The server
 * decides it from observation rather than a flag — a worker must have polled the lane inside 60 s —
 * so §3.14's ending is honest with nothing to switch on.
 */
export interface CompleteResponse {
  sessionId: string;
  path: "reveal" | "deferred";
  state: SessionState;
  /** The durable handle's URL, in full, ready to be shown in large type with a copy button (§3.13). */
  claimUrl: string;
  /** How many recordings the session ended with, and how many the plan asked for. */
  saved: number;
  planned: number;
  /** `reveal` only. §3.12's budget: 75 s p90 from the last recording to the first audio. */
  revealBudgetMs: number | null;
  /**
   * `deferred` only, and **for Karti, not for her.** She reads `FAMILY.deferred.*`; this is the
   * engineering sentence that goes to `/live` and into the notification (§3.14).
   */
  deferredBecause: string | null;
  /**
   * **Which of §3.14's three deferrals this is, so the family-facing sentence can be true.**
   *
   * `deferredBecause` above is a paragraph of engineering detail that VOX-LAW 16b forbids showing
   * her, so the screen needed *something* to choose copy from and had nothing — which is why
   * `FAMILY.deferred` said *"the computer that makes the voice is asleep"* for every case. As of
   * 2026-08-08 that is wrong in detail: spark-1 is up and reports `model_loaded: true`, and the
   * honest reasons a session still defers are different in kind. `deferredReasonCopy()` in
   * `shared/voice/copy.ts` maps this to the one warm sentence that is actually true.
   *
   * ⚠️ **Optional on the type, always present on the wire when `path === "deferred"`.** Optional so
   * that a reader written before this field existed still compiles against the same contract — the
   * same additive rule VOX-LAW 24 applies to the fleet manifest. A reader that does not find it
   * falls back to `FAMILY.deferred.after`, which is true in every case and specific in none.
   */
  deferredReason?: "queued" | "unavailable" | "not-configured" | null;
  health: HealthReport | null;
}

/**
 * `GET /c/:claimToken` answered as JSON — VOX-LAW 20's durable handle, resolved.
 *
 * Also previously declared twice: once in `server/src/routes/claim.ts` and once in
 * `client/src/lib/api.ts`. One route, two processes, one shape — so it belongs here.
 *
 * ⚠️ **This is the only route that turns a claim token into a session.** `GET /api/v1/sessions/:id`
 * refuses anything that is not a `ses_` id before it queries, so the token is presented as a
 * credential — this route once, then `x-vox-claim` on everything after.
 */
export interface ClaimView {
  session: SessionView;
  /** Null until `POST /complete` has run. `FAMILY.back.notReady` is the screen for that. */
  voice: VoiceSummary | null;
  /** So a page that arrived here can send it back on every later call without re-parsing its URL. */
  claimToken: string;
}

/**
 * `GET /api/v1/sessions/:id/links` — the three durable strings for one session.
 *
 * `CreateSessionResponse` carries the claim token exactly once, at creation, and `SessionView` does
 * not carry it at all. A durable handle that exists only in the tab that minted it is not durable,
 * and the screen that prints it is the most reloaded screen in the product. This is the route that
 * asks for it back.
 *
 * Every URL here is built by the SERVER against `publicOrigin`, never in the browser against
 * `window.location.origin`: through the tailnet this app answers on `100.92.185.76:8912`, and a claim
 * link printed with that in it is a link that works from exactly one house.
 */
export interface SessionLinks {
  /** `{publicOrigin}/c/{claimToken}` — VOX-LAW 20's durable handle, printed in large type. */
  claimUrl: string;
  /** The bare token, for the copy button's fallback and for reading aloud down a phone. */
  claimToken: string;
  /** `{publicOrigin}/live/{claimToken}` — §3.15. It carries the TOKEN: the helper has no cookie. */
  liveUrl: string;
}

/**
 * `GET /api/v1/sessions/:id/live` — SSE, read-only, for the session's helper (§3.15).
 *
 * One writer, one reader, no clock. The fleet already knows how to do this — movies has
 * authoritative-clock sync, audiobook has co-read rooms — and this is a strictly simpler case.
 *
 * ⚠️ **`error` carries FULL engineering detail and is for this stream only.** It is the one place in
 * the product where a stack trace is the right answer, because the reader is the person who can act
 * on it. VOX-LAW 16b governs the other end: the same failure renders to her as one warm sentence.
 */
export type LiveEvent =
  | { t: "state"; state: SessionState; at: number }
  | { t: "prompt"; promptId: string; index: number; total: number; at: number }
  | { t: "level"; peakDbfs: number; shortTermLufs: number; at: number }
  | { t: "coach"; line: string; spent: number; budget: number; at: number }
  | { t: "take"; take: TakeSummary; at: number }
  | { t: "drain"; saved: number; total: number; at: number }
  | { t: "error"; where: string; message: string; stack: string | null; at: number }
  | { t: "ended"; state: SessionState; at: number };

// ─── voices ──────────────────────────────────────────────────────────────────

export interface VoiceSummary {
  id: string;
  label: string;
  seedKind: SeedKind;
  status: VoiceStatus;
  consentState: ConsentState;
  /**
   * **Who owns it, or null — and it is on the SUMMARY because the list renders a chip from it.**
   *
   * ⚠️ `status` and ownership are different facts and the console conflated them: `CONSOLE.voice`
   * maps the lifecycle state `session` to the word *"Unclaimed"*, and `POST /voices/:id/claim`
   * deliberately writes `ownerUserId` without touching `status` (adoption is not publication). So a
   * claimed voice listed as UNCLAIMED. The list had no way to know better, because this field was on
   * `VoiceDetail` only — which is exactly the shape of gap that makes a summary lie.
   */
  ownerUserId: string | null;
  /** `sha256:…`, or null before anything has been delivered to a host. */
  currentRev: string | null;
  createdAt: number;
}

export interface VoiceDetail extends VoiceSummary {
  sessionId: string | null;
  /** Newest first. Append-only; VOX-LAW 7's high-water mark is derived from this, never from a listing. */
  revs: readonly VoiceRevSummary[];
  /** Measured WER on the consent recording, or null while the ASR job has not drained. */
  consentWer: number | null;
}

export interface VoiceRevSummary {
  rev: string;
  remoteId: string;
  hostUrl: string;
  seedSeconds: number;
  seedBytes: number;
  /** §8.5 — a DISTRIBUTION, not a number. Against 13.3–17.8% run-to-run variance a single value is
   *  either noisy or blind, and a canary that pages every week is muted by week three. */
  simNormMean: number | null;
  simNormSd: number | null;
  simNormRenders: number | null;
  publishedAt: number | null;
  withdrawnAt: number | null;
  withdrawnReason: string | null;
}

// ─── errors ──────────────────────────────────────────────────────────────────

/**
 * The only error envelope. `code` is for code, `detail` is for Karti, and **neither is ever shown to
 * the person recording** — VOX-LAW 16b routes every one of these to `FAMILY.mishap.keepGoing` and to
 * `/live`.
 *
 * **VOX-LAW 18: routes that serve bytes refuse 404, never 403.** A 403 on a voice id tells the caller
 * that the voice exists, which on this site is a statement about whose recordings are on this server.
 */
export interface ApiError {
  code: string;
  detail: string;
  /** Present when the failure belongs to a session, so `/live` and the push to Karti can join up. */
  sessionId?: string;
}

// ─── VOX-LAW 14: the readers ─────────────────────────────────────────────────

export class WireError extends Error {
  override name = "WireError";
}

const at = (path: string, key: string): string => (path ? `${path}.${key}` : key);

/**
 * A required number. **Throws on absent, null, NaN and Infinity.**
 *
 * `Number(undefined)` is `NaN`, `Number(null)` is `0`, and `??` treats `0` as present — three
 * different wrong answers from three idioms people reach for. This is the only one that is right, and
 * it is a function so that reaching for the wrong one is visible in review.
 */
export function num(o: unknown, key: string, path = ""): number {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (typeof v !== "number" || !Number.isFinite(v)) {
    throw new WireError(`${at(path, key)}: expected a finite number, got ${JSON.stringify(v)}`);
  }
  return v;
}

/** An optional number. Returns `null` when absent — **never 0**, which is a different measurement. */
export function optNum(o: unknown, key: string, path = ""): number | null {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (v === undefined || v === null) return null;
  if (typeof v !== "number" || !Number.isFinite(v)) {
    throw new WireError(`${at(path, key)}: expected a finite number or absent, got ${JSON.stringify(v)}`);
  }
  return v;
}

export function str(o: unknown, key: string, path = ""): string {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (typeof v !== "string" || v === "") {
    throw new WireError(`${at(path, key)}: expected a non-empty string, got ${JSON.stringify(v)}`);
  }
  return v;
}

export function optStr(o: unknown, key: string, path = ""): string | null {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (v === undefined || v === null || v === "") return null;
  if (typeof v !== "string") {
    throw new WireError(`${at(path, key)}: expected a string or absent, got ${JSON.stringify(v)}`);
  }
  return v;
}

/**
 * A required boolean. **Throws on absent**, which is the whole point: for the three DSP flags,
 * "the browser says false" and "the browser did not say" are different facts (VOX-LAW 2), and a
 * `?? false` at the call site turns the second into the first and makes an ineligible take eligible.
 */
export function bool(o: unknown, key: string, path = ""): boolean {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (typeof v !== "boolean") {
    throw new WireError(`${at(path, key)}: expected a boolean, got ${JSON.stringify(v)}`);
  }
  return v;
}

export function optBool(o: unknown, key: string, path = ""): boolean | null {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (v === undefined || v === null) return null;
  if (typeof v !== "boolean") {
    throw new WireError(`${at(path, key)}: expected a boolean or absent, got ${JSON.stringify(v)}`);
  }
  return v;
}

/** A required member of a closed set. The error names the whole set, so a typo is self-diagnosing. */
export function enumOf<T extends string>(o: unknown, key: string, allowed: readonly T[], path = ""): T {
  const v = (o as Record<string, unknown> | null)?.[key];
  if (typeof v !== "string" || !(allowed as readonly string[]).includes(v)) {
    throw new WireError(
      `${at(path, key)}: expected one of ${allowed.join("|")}, got ${JSON.stringify(v)}`,
    );
  }
  return v as T;
}
